import { SubscriptionInfo, SubscriptionPlan, SubscriptionStatus } from '../types';

const STORAGE_KEY = 'allodos-subscription';

export type BackendSubscriptionSnapshot = {
  trialStartDate?: string | Date | null;
  trialEndDate?: string | Date | null;
  subscriptionStatus?: string | null;
  subscriptionEndDate?: string | Date | null;
};

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const farFutureDate = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 10);
  return date;
};

export const subscriptionService = {
  getSubscription(): SubscriptionInfo {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const sub = JSON.parse(saved) as SubscriptionInfo;
      // Recalculate status based on dates
      return this.refreshStatus(sub);
    }

    const now = new Date();
    const endDate = farFutureDate();

    const newSub: SubscriptionInfo = {
      plan: SubscriptionPlan.MONTHLY,
      status: SubscriptionStatus.ACTIVE,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      trialStartDate: now.toISOString(),
      trialEndDate: undefined,
      isAutoRenew: true
    };

    this.saveSubscription(newSub);
    return newSub;
  },

  saveSubscription(sub: SubscriptionInfo) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sub));
  },

  syncFromBackend(snapshot: BackendSubscriptionSnapshot): SubscriptionInfo {
    const now = new Date();
    const backendStatus = String(snapshot.subscriptionStatus ?? '').toUpperCase();
    if (!backendStatus) return this.getSubscription();

    const trialStartDate = toDate(snapshot.trialStartDate);
    const trialEndDate = toDate(snapshot.trialEndDate);
    const subscriptionEndDate = toDate(snapshot.subscriptionEndDate);
    const endDate = subscriptionEndDate ?? trialEndDate ?? farFutureDate();

    const sub: SubscriptionInfo = {
      plan: SubscriptionPlan.MONTHLY,
      status: SubscriptionStatus.ACTIVE,
      startDate: trialStartDate?.toISOString() ?? now.toISOString(),
      endDate: endDate.toISOString(),
      trialStartDate: trialStartDate?.toISOString(),
      trialEndDate: trialEndDate?.toISOString(),
      isAutoRenew: backendStatus === 'ACTIVE',
    };

    const refreshed = this.refreshStatus(sub);
    this.saveSubscription(refreshed);
    return refreshed;
  },

  refreshStatus(sub: SubscriptionInfo): SubscriptionInfo {
    // For Beta version, we keep the status active and ignore expiration
    if (sub.status === SubscriptionStatus.EXPIRED || sub.status === SubscriptionStatus.GRACE_PERIOD) {
      sub.status = SubscriptionStatus.ACTIVE;
      this.saveSubscription(sub);
    }
    return sub;
  },

  getRemainingDays(sub: SubscriptionInfo): number {
    return 999; // Indefinite for Beta
  },

  getRetentionDays(sub: SubscriptionInfo): number {
    return 999;
  },

  subscribe(plan: SubscriptionPlan) {
    const now = new Date();
    const endDate = new Date();
    endDate.setFullYear(now.getFullYear() + 10); // Far future for Beta

    const sub: SubscriptionInfo = {
      plan,
      status: SubscriptionStatus.ACTIVE,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      lastPaymentDate: now.toISOString(),
      isAutoRenew: true
    };

    this.saveSubscription(sub);
    return sub;
  },

  shouldBlockAccess(sub: SubscriptionInfo): boolean {
    return false; // Never block in Beta
  },

  shouldShowRetentionWarning(sub: SubscriptionInfo): boolean {
    return false;
  },

  shouldDeleteData(sub: SubscriptionInfo): boolean {
    return false;
  }
};
